import React, { useState, useEffect } from "react";
import SEO from "../seo";

const ChartsAndGraphics = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "how-to-access",
        "data-update-frequency",
        "key-metrics-tracked",
        "dashboard-overview",
        "sales-analytics",
        "conversion-analytics",
        "product-customer-analytics",
        "sessions-visitor-tracking",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Charts and Graphics | xPage Analytics Help Center"
        description="Learn how to use Xpage Analytics Charts and Graphics to visualize your store performance. Track sales, conversions, customer behavior, and traffic sources with interactive dashboards."
        keywords="xPage, analytics, charts, graphics, data visualization, sales analytics, conversion tracking, customer analytics, store performance, metrics, dashboard"
        canonical="https://help.xpage.ai/help/business-expansion/Charts%20and%20Graphics"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Charts and Graphics
          </h1>
          <p className="text-sm text-systemLabel">
            Xpage Analytics is your central hub for understanding your online
            store's performance. Designed to be intuitive and actionable, the
            dashboard gives you the insights you need to make informed,
            data-driven decisions to grow your business.
          </p>
        </header>

        {/* How to Access Section */}
        <section id="how-to-access" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How to Access
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-decimal list-inside space-y-1 text-sm text-systemLabel">
              <li>Log in to your Xpage Admin.</li>
              <li>
                Click Analytics in the left sidebar to enter the Analytics
                Overview.
              </li>
            </ul>
          </div>
        </section>

        {/* Data Update Frequency Section */}
        <section id="data-update-frequency" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Data Update Frequency
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>
                Analytics data is updated in real-time, with a refresh every 4–5
                seconds.
              </li>
              <li>
                You can refresh the page at any time to see the latest
                information.
              </li>
            </ul>
          </div>
        </section>

        {/* Key Metrics Tracked Section */}
        <section id="key-metrics-tracked" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Key Metrics Tracked
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Xpage Analytics provides a complete view of your store's
              performance, including:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>Sales – Track total revenue and trends.</li>
              <li>Orders – Monitor order counts over time.</li>
              <li>
                Conversion Rate – Measure the percentage of visitors who
                complete purchases.
              </li>
              <li>
                Reached Cart – Track how many visitors add items to the cart.
              </li>
              <li>
                Initial Checkout – Monitor how many begin the checkout process.
              </li>
              <li>Overall Visitors – See total visitors to your store.</li>
              <li>
                Top Selling Locations – Identify the geographic areas generating
                the most sales.
              </li>
              <li>
                Average Order Value (AOV) – Track the average value of customer
                orders.
              </li>
              <li>Returning Customers Rate – Measure customer retention.</li>
              <li>Customers – Track the total number of unique customers.</li>
              <li>
                Top Selling Products – See which products generate the most
                revenue.
              </li>
              <li>
                Visitors by Device Type – Understand what devices your customers
                use.
              </li>
              <li>
                Top Referrer Sources – Identify where traffic is coming from.
              </li>
              <li>Visitors by Social Media – Analyze social traffic trends.</li>
              <li>
                Sales by Referrer Traffic – Measure revenue from different
                referral sources.
              </li>
              <li>
                Sales by Social Media Traffic – Track revenue generated through
                social channels.
              </li>
            </ul>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                Each metric is tracked over the last 30 days by default, with
                comparisons to the previous period. You can adjust the date
                range and apply filters to analyze specific segments.
              </p>
            </div>
          </div>
        </section>

        {/* Dashboard Overview Section */}
        <section id="dashboard-overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Dashboard Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              The Dashboard Overview provides a snapshot of your key metrics in
              one place:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mb-4">
              <li>
                Cards highlight critical statistics like Total Sales, Orders,
                Customers, and Conversion Rate.
              </li>
              <li>
                Graphs show trends over time, including revenue growth, visitor
                sessions, and order volume.
              </li>
              <li>
                Conversion Funnel visualizes the customer journey from visiting
                the store to reaching checkout, helping you identify drop-off
                points.
              </li>
            </ul>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              What You Can Gain:
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>
                Quick insights into your store's Key Performance Indicators
                (KPIs).
              </li>
              <li>
                Visual understanding of the conversion funnel to optimize the
                customer journey.
              </li>
              <li>Real-time data to make informed decisions.</li>
            </ul>
          </div>
        </section>

        {/* Sales Analytics Section */}
        <section id="sales-analytics" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Sales Analytics
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              The Sales module focuses on your revenue performance:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mb-4">
              <li>Total Sales – Overall revenue generated.</li>
              <li>
                Sales by Referrer Traffic – See which sources drive revenue.
              </li>
              <li>
                Sales by Social Media Traffic – Track sales generated from
                social platforms.
              </li>
              <li>
                Average Order Value (AOV) – Monitor the average revenue per
                order.
              </li>
            </ul>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              What You Can Gain:
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>Monitor growth and revenue trends.</li>
              <li>Identify high-performing traffic sources.</li>
              <li>Understand sales performance at a glance.</li>
            </ul>
          </div>
        </section>

        {/* Conversion Analytics Section */}
        <section id="conversion-analytics" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Conversion Analytics
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Analyze how effectively your store converts visitors into buyers:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mb-4">
              <li>
                Conversion Rate – Percentage of visitors completing a purchase.
              </li>
              <li>
                Reached Cart & Initial Checkout – Track the steps leading up to
                purchase.
              </li>
              <li>
                Returning Customer Rate – Understand customer loyalty and repeat
                purchases.
              </li>
            </ul>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              What You Can Gain:
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>Identify bottlenecks in your sales funnel.</li>
              <li>
                Optimize landing pages and product pages for higher conversions.
              </li>
            </ul>
          </div>
        </section>

        {/* Product & Customer Analytics Section */}
        <section id="product-customer-analytics" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Product & Customer Analytics
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mb-4">
              <li>
                Top Selling Products – Discover which products generate the most
                sales.
              </li>
              <li>
                Top Selling Locations – Geographic distribution of your
                customers.
              </li>
              <li>Customers – Track new vs. returning customers.</li>
              <li>
                Visitors by Device Type – Know which devices your customers use.
              </li>
              <li>
                Top Referrer Sources & Social Media – See where your visitors
                come from.
              </li>
            </ul>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              What You Can Gain:
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>Identify best-selling products and regions.</li>
              <li>
                Understand customer behavior by device and referral source.
              </li>
              <li>
                Optimize marketing efforts based on traffic and sales data.
              </li>
            </ul>
          </div>
        </section>

        {/* Sessions & Visitor Tracking Section */}
        <section id="sessions-visitor-tracking" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Sessions & Visitor Tracking
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Xpage tracks sessions and visitors using cookies:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mb-4">
              <li>Visitor cookie – Identifies unique devices.</li>
              <li>Session cookie – Tracks each session's activity.</li>
            </ul>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Session Ends When:
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mb-4">
              <li>No activity occurs for 30 minutes.</li>
              <li>
                Any session not closed by midnight UTC is automatically ended.
              </li>
            </ul>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mb-4">
              <p className="text-sm text-systemLabel">
                Note: A single visitor can trigger multiple sessions if they
                return to the store multiple times.
              </p>
            </div>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Example:
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mb-4">
              <li>
                A visitor browses for 20 minutes, leaves, and returns two hours
                later → 1 visitor, 2 sessions.
              </li>
              <li>
                A visitor leaves after 5 minutes and returns 10 minutes later →
                1 visitor, 1 session.
              </li>
            </ul>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              What You Can Gain:
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>Accurate tracking of visitor behavior.</li>
              <li>Insights to optimize engagement and sales performance.</li>
            </ul>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                Xpage Analytics gives you actionable insights across sales,
                products, customers, and traffic sources, empowering you to make
                data-driven decisions that grow your store.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            {[
              { id: "overview", label: "Overview" },
              { id: "how-to-access", label: "How to Access" },
              { id: "data-update-frequency", label: "Data Update Frequency" },
              { id: "key-metrics-tracked", label: "Key Metrics Tracked" },
              { id: "dashboard-overview", label: "Dashboard Overview" },
              { id: "sales-analytics", label: "Sales Analytics" },
              { id: "conversion-analytics", label: "Conversion Analytics" },
              {
                id: "product-customer-analytics",
                label: "Product & Customer Analytics",
              },
              {
                id: "sessions-visitor-tracking",
                label: "Sessions & Visitor Tracking",
              },
            ].map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={`block py-1 transition-colors border-l-2 pl-3 ${
                    activeSection === item.id
                      ? "text-deepPurple font-semibold border-deepPurple"
                      : "border-transparent hover:text-deepPurple"
                  }`}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChartsAndGraphics;
